import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useDreamReview, REST_LABEL, own } from '../../data/dashboard';
import type { DreamCandidate, DreamReviewGroup, DreamKeyMoment } from '../../data/dashboard';

const MONO = REXY_FONT;
const DREAM_OK = '#00FF9C';

// ─────────────────────────────────────────────────────────────────────────────
// DREAM REVIEW — THE OWNER DREAM GATE'S SCREEN (owner rulings G1–G12, 20 Oct;
// U1–U4, 21 Oct). What her nights dream waits here until he decides it: each
// candidate VERBATIM, grouped by the night that dreamed it (newest first), with
// when it was formed, the days it rests on and compactly what it rests on (G4),
// and his two buttons — Confirm or Reject, one click, no reason (G2). A decision
// shows as made only once the server says so; the list then reloads. Below, her
// key moments — what he has confirmed — read-only.
//
// THE DESKTOP LAYOUT ONLY (owner ruling 22 Oct, replacing G12's desktop-app-only):
// mounted inside DreamstatePanel wherever he opens the desktop layout — his browser
// and the Electron window alike. The phone surface never mounts it, so it never
// fetches it either.
// ─────────────────────────────────────────────────────────────────────────────

/** The desktop layout, in a browser or the Electron window — never the phone surface.
 *  App.tsx tells the two apart once per document: `?surface=phone` (what the iOS app
 *  loads) mounts PhoneHost instead of the desktop layout. The same test, asked here. */
export function isDesktopApp(): boolean {
  return typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('surface') !== 'phone';
}

function restsOn(rests: Record<string, number>): string {
  return Object.keys(rests || {})
    .filter((k) => Object.prototype.hasOwnProperty.call(rests, k))
    .map((k) => `${own(REST_LABEL, k) || k} ${rests[k]}`)
    .join(' · ');
}

export default function DreamReviewSection() {
  const { view, err, deciding, notice, decide } = useDreamReview();

  function renderCandidate(c: DreamCandidate) {
    const busy = deciding !== null;
    return (
      <div key={c.id} style={styles.candidate} title={`${c.id} · ${c.revision}`}>
        <div style={styles.candHeader}>
          <span style={styles.topic}>{c.topic}</span>
          <span style={styles.kind}>{c.kind}</span>
          {c.seen ? <span style={styles.seen}>seen {c.seen}</span> : null}
        </div>
        <div style={styles.meta}>
          formed {c.formed_label} {'·'} rests on {c.days_label}
        </div>
        {/* What her night wrote, VERBATIM — he decides on exactly this. */}
        <div style={styles.text}>{c.text}</div>
        {c.his_words.length > 0 ? (
          <div style={styles.words}>
            {c.his_words.map((w, i) => <div key={i}>{'“'}{w}{'”'}</div>)}
          </div>
        ) : null}
        <div style={styles.meta}>{restsOn(c.rests_on)}</div>
        {c.replaces ? (
          <div style={styles.replaces}>
            <span style={styles.replacesLabel}>in place of the key moment</span> {c.replaces.text}
          </div>
        ) : null}
        <div style={styles.actions}>
          <button type="button" style={{ ...styles.button, ...styles.confirm }}
                  disabled={busy} onClick={() => decide(c, 'confirm')}>
            {deciding === c.id ? '…' : 'Confirm'}
          </button>
          <button type="button" style={{ ...styles.button, ...styles.reject }}
                  disabled={busy} onClick={() => decide(c, 'reject')}>
            Reject
          </button>
        </div>
      </div>
    );
  }

  function renderGroup(g: DreamReviewGroup) {
    const n = g.candidates.length;
    return (
      <div key={g.run_id || g.formed_at} style={styles.group} title={g.run_id || g.formed_at}>
        <div style={styles.groupHeader}>
          <span style={styles.groupLabel}>{g.formed_label}</span>
          {g.night ? <span style={styles.groupNight}>night of {g.night}</span> : null}
          <span style={styles.count}>{n} waiting</span>
        </div>
        <div style={styles.list}>{g.candidates.map(renderCandidate)}</div>
      </div>
    );
  }

  function renderKey(k: DreamKeyMoment) {
    return (
      <div key={k.id} style={styles.keyRow} title={k.id}>
        <div style={styles.candHeader}>
          <span style={styles.topic}>{k.topic}</span>
          <span style={styles.kind}>{k.kind}</span>
        </div>
        <div style={styles.meta}>
          confirmed {k.confirmed_label} {'·'} last supported {k.last_supported_label}
        </div>
        <div style={styles.text}>{k.text}</div>
      </div>
    );
  }

  return (
    <div style={styles.section} data-testid="dream-review">
      <div style={styles.headerRow}>
        <span style={styles.header}>DREAM REVIEW</span>
        {view ? <span style={styles.subtitle}>{view.pending_total} waiting for you</span> : null}
        {notice ? (
          <span role="status"
                style={{ ...styles.notice, color: notice.kind === 'done' ? DREAM_OK : REXY_CONSOLE.warning }}>
            {notice.text} {'·'} {notice.topic}
          </span>
        ) : null}
      </div>
      {err ? (
        <div style={styles.degraded}>not_available {'—'} {err}</div>
      ) : !view ? (
        <div style={styles.degraded}>loading{'…'}</div>
      ) : (
        <>
          {view.groups.length === 0 ? (
            <div style={styles.empty}>nothing waiting for you</div>
          ) : (
            <div style={styles.groups}>{view.groups.map(renderGroup)}</div>
          )}
          <div style={styles.keys}>
            <div style={styles.keysHeader}>KEY MOMENTS {'·'} {view.key_moments.length}</div>
            {view.key_moments.length === 0 ? (
              <div style={styles.empty}>no key moments yet</div>
            ) : (
              <div style={styles.list}>{view.key_moments.map(renderKey)}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { borderTop: `1px solid ${REXY_CONSOLE.divider}`, marginTop: 10, paddingTop: 8 },
  headerRow: { display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6, minWidth: 0 },
  header: { fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: 2, color: REXY_CONSOLE.primary },
  subtitle: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.dimText },
  notice: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, marginLeft: 'auto', overflowWrap: 'anywhere' },
  groups: { display: 'flex', flexDirection: 'column', gap: 10 },
  group: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, padding: '8px 4px 4px 4px' },
  groupHeader: { display: 'flex', alignItems: 'baseline', gap: 8 },
  groupLabel: { fontFamily: MONO, fontSize: 11, color: REXY_CONSOLE.primary, whiteSpace: 'nowrap' },
  groupNight: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.mutedText, whiteSpace: 'nowrap' },
  count: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, marginLeft: 'auto' },
  list: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 },
  candidate: { borderLeft: `2px solid ${REXY_CONSOLE.gold}`, paddingLeft: 8, paddingBottom: 2 },
  keyRow: { borderLeft: `2px solid ${DREAM_OK}`, paddingLeft: 8, paddingBottom: 2 },
  candHeader: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 2 },
  topic: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.primary, overflowWrap: 'anywhere' },
  kind: { fontFamily: MONO, fontSize: 8, letterSpacing: 1, color: REXY_CONSOLE.dimText },
  seen: { fontFamily: MONO, fontSize: 8, color: REXY_CONSOLE.dimText, marginLeft: 'auto', whiteSpace: 'nowrap' },
  meta: { fontFamily: MONO, fontSize: 8, color: REXY_CONSOLE.mutedText, marginBottom: 3, overflowWrap: 'anywhere' },
  text: { fontFamily: MONO, fontSize: 10, lineHeight: 1.55, color: REXY_CONSOLE.infText, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', marginBottom: 3 },
  words: { fontFamily: MONO, fontSize: 9, lineHeight: 1.5, color: REXY_CONSOLE.mutedText, marginBottom: 3, overflowWrap: 'anywhere' },
  replaces: { fontFamily: MONO, fontSize: 9, lineHeight: 1.5, color: REXY_CONSOLE.mutedText, marginBottom: 3, overflowWrap: 'anywhere' },
  replacesLabel: { color: REXY_CONSOLE.gold, letterSpacing: 1 },
  actions: { display: 'flex', gap: 8, marginTop: 4 },
  button: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, padding: '3px 10px', borderRadius: 4, background: REXY_CONSOLE.cardBg, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)' },
  confirm: { border: `1px solid ${DREAM_OK}`, color: DREAM_OK },
  reject: { border: `1px solid ${REXY_CONSOLE.critical}`, color: REXY_CONSOLE.critical },
  keys: { borderTop: `1px solid ${REXY_CONSOLE.rowDivider}`, marginTop: 10, padding: '8px 4px 4px 4px' },
  keysHeader: { fontFamily: MONO, fontSize: 9, letterSpacing: 1, color: REXY_CONSOLE.mutedText },
  degraded: { fontFamily: MONO, fontSize: 10, color: REXY_CONSOLE.dimText, padding: '4px 0' },
  empty: { fontFamily: MONO, fontSize: 9, color: REXY_CONSOLE.dimText, padding: '4px 0' },
};
